import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { useApiConfig } from '@hooks/useApiConfig';
import { useTranslation } from 'react-i18next';

const ENDPOINT_TEMPLATE = '/profile/delete-vehicle/{uuid}';
const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_TEMPLATE}` as const;

export const useDeleteVehicleRequest = () => {
    const { t } = useTranslation();
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const request = async (uuid: string) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const endpoint = ENDPOINT_TEMPLATE.replace('{uuid}', uuid);

        try {
            const params = new URLSearchParams(getCommonParameters()).toString();

            const response = await fetch(`${API_URL}${endpoint}?${params}`, {
                method: 'DELETE',
                headers: getHeaders(),
            });

            const contentType = response.headers.get('content-type');
            const data = contentType && contentType.includes('application/json') ? await response.json() : null;

            if (!response.ok) {
                setError(data?.message || t('common.error-occurred'));
                return;
            }

            setSuccess(data?.message || t('common.success'));
        } catch (err: any) {
            handleCommonErrors(err);
            err?.message && setError(err.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return {
        request,
        loading,
        error,
        success,
        lastResponseTime,
    };
};
